import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from './axiosInstance';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Add, DeleteOutline, EditOutlined, Search } from '@mui/icons-material';
import './ExportGridView.css';

const rowsPerPage = 10;

const blipColumnWidths = {
  full_name: '12%',
  job_title: '12%',
  location: '11%',
  clock_in_date: '8%',
  clock_in_location: '10%',
  clock_out_date: '8%',
  clock_out_location: '10%',
  blip_type: '7%',
  hours: '5%',
  hours_paid: '6%',
  hourly_rate: '7%',
  total_amount: '7%',
  actions: '8%',
};

const defaultFormState = {
  first_name: '',
  last_name: '',
  job_title: '',
  team: '',
  blip_type: 'Shift',
  clock_in_date: '',
  clock_in_time: '',
  clock_in_location: '',
  clock_in_location_id: '',
  clock_out_date: '',
  clock_out_time: '',
  clock_out_location: '',
  clock_out_location_id: '',
  payroll_reference: '',
  hourly_rate: '',
  total_duration: '',
  total_excluding_breaks: '',
  total_amount: '',
  notes: '',
};

const formatCurrency = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '£0.00';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(number);
};

const formatHours = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0.00';
  return number.toFixed(2);
};

const toDateInputValue = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  const slashMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (slashMatch) {
    let first = Number(slashMatch[1]);
    let second = Number(slashMatch[2]);
    let year = Number(slashMatch[3]);
    if (year < 100) year += 2000;
    let day = first;
    let month = second;
    if (first <= 12 && second > 12) {
      day = second;
      month = first;
    }
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  return text;
};

const formatDisplayDate = (value) => {
  const inputValue = toDateInputValue(value);
  const match = inputValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return String(value || '').trim();
  return `${match[3]}/${match[2]}/${match[1]}`;
};

const formatDateTime = (dateValue, timeValue) => {
  const dateText = formatDisplayDate(dateValue);
  const timeText = String(timeValue || '').trim();
  if (dateText && timeText) return `${dateText} ${timeText}`;
  return dateText || timeText || '-';
};

const getLocationLabel = (location, fallback = 'Unassigned') => {
  if (!location) return fallback;
  return location.name || location.location_id || fallback;
};

const ManageBlipInPage = () => {
  const [records, setRecords] = useState([]);
  const [locations, setLocations] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [nameSearchInput, setNameSearchInput] = useState('');
  const [nameSearchQuery, setNameSearchQuery] = useState('');
  const [jobTitleSearchInput, setJobTitleSearchInput] = useState('');
  const [jobTitleSearchQuery, setJobTitleSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [jobTitleFilter, setJobTitleFilter] = useState('all');
  const [sortKey, setSortKey] = useState('clock_in_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(0);
  const [pageInput, setPageInput] = useState('1');
  const [totalRecords, setTotalRecords] = useState(0);
  const [netTotalAmount, setNetTotalAmount] = useState(0);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [savingRecord, setSavingRecord] = useState(false);
  const [deletingRecordId, setDeletingRecordId] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [recordId, setRecordId] = useState('');
  const [formData, setFormData] = useState(defaultFormState);
  const [formError, setFormError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const latestRequestRef = useRef(0);
  const previousSearchRef = useRef({ name: '', jobTitle: '' });

  const sortedLocations = useMemo(
    () => [...locations].sort((left, right) => getLocationLabel(left).localeCompare(getLocationLabel(right))),
    [locations],
  );

  const sortedJobTitles = useMemo(
    () => [...jobTitles].sort((left, right) => (left?.label || '').localeCompare(right?.label || '')),
    [jobTitles],
  );

  const showToast = useCallback((message, severity = 'success') => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('hv-toast', { detail: { message, severity } }));
  }, []);

  const loadOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const [locationsResponse, jobTitlesResponse] = await Promise.all([
        axiosInstance.get('/api/v1/location'),
        axiosInstance.get('/api/v1/manage/job-titles', { params: { page: 1, limit: 1000, sort_key: 'job_title', sort_direction: 'asc' } }),
      ]);
      const locationPayload = locationsResponse?.data?.data ?? locationsResponse?.data ?? [];
      const jobTitlePayload = jobTitlesResponse?.data?.data ?? [];
      setLocations(Array.isArray(locationPayload) ? locationPayload : []);
      setJobTitles(
        (Array.isArray(jobTitlePayload) ? jobTitlePayload : []).map((entry) => ({
          value: entry?.normalized_job_title || '',
          label: entry?.job_title || entry?.normalized_job_title || '',
          is_labour: Boolean(entry?.is_labour),
        })).filter((entry) => entry.value && entry.label),
      );
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Failed to load filter options.', 'error');
    } finally {
      setLoadingOptions(false);
    }
  }, [showToast]);

  const loadRecords = useCallback(async (
    pageIndex = page,
    nextSortKey = sortKey,
    nextSortDirection = sortDirection,
    nextLocationFilter = locationFilter,
    nextJobTitleFilter = jobTitleFilter,
    nextNameSearchQuery = nameSearchQuery,
    nextJobTitleSearchQuery = jobTitleSearchQuery,
    nextStartDate = startDate,
    nextEndDate = endDate,
  ) => {
    const requestId = ++latestRequestRef.current;
    setLoadingRecords(true);
    try {
      const params = {
        page: pageIndex + 1,
        limit: rowsPerPage,
        sort_key: nextSortKey,
        sort_direction: nextSortDirection,
      };
      if (nextLocationFilter !== 'all') {
        params.location_id = nextLocationFilter;
      }
      if (nextJobTitleFilter !== 'all') {
        params.job_title = nextJobTitleFilter;
      }
      if (nextNameSearchQuery?.trim()) {
        params.search_name = nextNameSearchQuery.trim();
      }
      if (nextJobTitleSearchQuery?.trim()) {
        params.search_job_title = nextJobTitleSearchQuery.trim();
      }
      if (nextStartDate) {
        params.start_date = nextStartDate;
      }
      if (nextEndDate) {
        params.end_date = nextEndDate;
      }

      const response = await axiosInstance.get('/api/v1/manage/blip-in', { params });
      if (requestId !== latestRequestRef.current) return;
      const payload = response?.data ?? {};
      const nextRecords = Array.isArray(payload?.data) ? payload.data : [];
      setRecords(nextRecords);
      setTotalRecords(Number(payload?.total ?? nextRecords.length ?? 0));
      setNetTotalAmount(Number(payload?.net_total_amount ?? 0));
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      showToast(error?.response?.data?.error || error?.message || 'Failed to load Blip In records.', 'error');
    } finally {
      if (requestId !== latestRequestRef.current) return;
      setLoadingRecords(false);
    }
  }, [endDate, jobTitleFilter, jobTitleSearchQuery, locationFilter, nameSearchQuery, page, showToast, sortDirection, sortKey, startDate]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    const nextSearchState = { name: nameSearchQuery, jobTitle: jobTitleSearchQuery };
    if (
      previousSearchRef.current.name !== nextSearchState.name
      || previousSearchRef.current.jobTitle !== nextSearchState.jobTitle
    ) {
      previousSearchRef.current = nextSearchState;
      if (page !== 0) {
        setPage(0);
        return;
      }
    }

    loadRecords(page, sortKey, sortDirection, locationFilter, jobTitleFilter, nameSearchQuery, jobTitleSearchQuery, startDate, endDate);
  }, [loadRecords, page, sortKey, sortDirection, locationFilter, jobTitleFilter, nameSearchQuery, jobTitleSearchQuery, startDate, endDate]);

  useEffect(() => {
    setPageInput(String(page + 1));
  }, [page, totalRecords]);

  const openCreateDialog = () => {
    setFormMode('create');
    setRecordId('');
    setFormData(defaultFormState);
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (record) => {
    setFormMode('edit');
    setRecordId(record?.id || '');
    setFormData({
      first_name: record?.first_name || '',
      last_name: record?.last_name || '',
      job_title: record?.job_title || '',
      team: record?.team || '',
      blip_type: record?.blip_type || 'Shift',
      clock_in_date: toDateInputValue(record?.clock_in_date),
      clock_in_time: record?.clock_in_time || '',
      clock_in_location: record?.clock_in_location || '',
      clock_in_location_id: record?.clock_in_location_id || '',
      clock_out_date: toDateInputValue(record?.clock_out_date),
      clock_out_time: record?.clock_out_time || '',
      clock_out_location: record?.clock_out_location || '',
      clock_out_location_id: record?.clock_out_location_id || '',
      payroll_reference: record?.payroll_reference || '',
      hourly_rate: record?.hourly_rate ?? '',
      total_duration: record?.total_duration || '',
      total_excluding_breaks: record?.total_excluding_breaks || '',
      total_amount: record?.total_amount ?? '',
      notes: record?.notes || '',
    });
    setFormError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (savingRecord) return;
    setDialogOpen(false);
    setFormError('');
    setFormData(defaultFormState);
    setFormMode('create');
    setRecordId('');
  };

  const openDeleteDialog = (record) => {
    setDeleteTarget(record);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deletingRecordId) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleFormChange = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.first_name.trim()) return 'First name is required.';
    if (!formData.last_name.trim()) return 'Last name is required.';
    if (!formData.job_title.trim()) return 'Job title is required.';
    if (!formData.blip_type.trim()) return 'Blip type is required.';
    return '';
  };

  const buildPayload = () => ({
    first_name: formData.first_name.trim(),
    last_name: formData.last_name.trim(),
    job_title: formData.job_title.trim(),
    team: formData.team.trim(),
    blip_type: formData.blip_type.trim(),
    clock_in_date: formData.clock_in_date,
    clock_in_time: formData.clock_in_time,
    clock_in_location_id: formData.clock_in_location_id,
    clock_out_date: formData.clock_out_date,
    clock_out_time: formData.clock_out_time,
    clock_out_location_id: formData.clock_out_location_id,
    payroll_reference: formData.payroll_reference.trim(),
    hourly_rate: formData.hourly_rate,
    total_duration: formData.total_duration,
    total_excluding_breaks: formData.total_excluding_breaks,
    total_amount: formData.total_amount,
    notes: formData.notes,
  });

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSavingRecord(true);
    setFormError('');
    try {
      const payload = buildPayload();
      if (formMode === 'edit' && recordId) {
        await axiosInstance.put(`/api/v1/manage/blip-in/${recordId}`, payload);
        showToast('Blip In record updated successfully.', 'success');
      } else {
        await axiosInstance.post('/api/v1/manage/blip-in', payload);
        showToast('Blip In record created successfully.', 'success');
      }
      closeDialog();
      await loadRecords(page, sortKey, sortDirection, locationFilter, jobTitleFilter);
    } catch (error) {
      setFormError(error?.response?.data?.error || error?.message || 'Failed to save blip record.');
      showToast(error?.response?.data?.error || error?.message || 'Failed to save blip record.', 'error');
    } finally {
      setSavingRecord(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeletingRecordId(deleteTarget.id);
    try {
      await axiosInstance.delete(`/api/v1/manage/blip-in/${deleteTarget.id}`);
      showToast('Blip In record deleted successfully.', 'success');
      closeDeleteDialog();
      await loadRecords(page, sortKey, sortDirection, locationFilter, jobTitleFilter);
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Failed to delete blip record.', 'error');
    } finally {
      setDeletingRecordId('');
    }
  };

  const handleSort = (columnKey) => {
    const isAsc = sortKey === columnKey && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortKey(columnKey);
    setPage(0);
  };

  const handlePageChange = (_event, nextPage) => {
    setPage(nextPage);
  };

  const handlePageInputChange = (event) => {
    const nextValue = event.target.value.replace(/[^0-9]/g, '');
    setPageInput(nextValue);
  };

  const handlePageInputKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    const nextPage = Math.min(Math.max(Number(pageInput || 1), 1), Math.max(Math.ceil(totalRecords / rowsPerPage), 1));
    setPage(nextPage - 1);
  };

  const handleLocationFilterChange = (value) => {
    setLocationFilter(value);
    setPage(0);
  };

  const handleJobTitleFilterChange = (value) => {
    setJobTitleFilter(value);
    setPage(0);
  };

  const applyNameSearch = useCallback(() => {
    setPage(0);
    setNameSearchQuery(nameSearchInput.trim());
  }, [nameSearchInput]);

  const applyJobTitleSearch = useCallback(() => {
    setPage(0);
    setJobTitleSearchQuery(jobTitleSearchInput.trim());
  }, [jobTitleSearchInput]);

  const handleNameSearchKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    applyNameSearch();
  };

  const handleJobTitleSearchKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    applyJobTitleSearch();
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
    setPage(0);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
    setPage(0);
  };

  const hasActiveFilters = Boolean(
    nameSearchInput
    || nameSearchQuery
    || jobTitleSearchInput
    || jobTitleSearchQuery
    || startDate
    || endDate
    || locationFilter !== 'all'
    || jobTitleFilter !== 'all'
  );

  const clearFilters = () => {
    setNameSearchInput('');
    setNameSearchQuery('');
    setJobTitleSearchInput('');
    setJobTitleSearchQuery('');
    setStartDate('');
    setEndDate('');
    setLocationFilter('all');
    setJobTitleFilter('all');
    setPage(0);
  };

  const displayRows = records;
  const totalPages = Math.max(Math.ceil(totalRecords / rowsPerPage), 1);
  const showingStart = totalRecords ? page * rowsPerPage + 1 : 0;
  const showingEnd = totalRecords ? Math.min((page + 1) * rowsPerPage, totalRecords) : 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 500 }}>
              Manage Blip In
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
            <Box sx={{ minWidth: 280 }}>
              <TextField
                value={nameSearchInput}
                onChange={(event) => setNameSearchInput(event.target.value)}
                onKeyDown={handleNameSearchKeyDown}
                label="Search Name"
                size="small"
                placeholder="Search first or last name"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label="search name" edge="end" onClick={applyNameSearch}>
                        <Search />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ minWidth: 280 }}>
              <TextField
                value={jobTitleSearchInput}
                onChange={(event) => setJobTitleSearchInput(event.target.value)}
                onKeyDown={handleJobTitleSearchKeyDown}
                label="Search Job Title"
                size="small"
                placeholder="Search job title"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label="search job title" edge="end" onClick={applyJobTitleSearch}>
                        <Search />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ minWidth: 180 }}>
              <TextField
                label="Clock In Start"
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box sx={{ minWidth: 180 }}>
              <TextField
                label="Clock In End"
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box sx={{ minWidth: 220 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="blip-location-filter-label">Location</InputLabel>
                <Select
                  labelId="blip-location-filter-label"
                  value={locationFilter}
                  label="Location"
                  onChange={(event) => handleLocationFilterChange(event.target.value)}
                >
                  <MenuItem value="all">All Locations</MenuItem>
                  {sortedLocations.map((location) => (
                    <MenuItem key={location.location_id || location.name} value={location.location_id || ''}>
                      {getLocationLabel(location)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ minWidth: 220 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="blip-job-filter-label">Job Title</InputLabel>
                <Select
                  labelId="blip-job-filter-label"
                  value={jobTitleFilter}
                  label="Job Title"
                  onChange={(event) => handleJobTitleFilterChange(event.target.value)}
                >
                  <MenuItem value="all">All Job Titles</MenuItem>
                  {sortedJobTitles.map((jobTitle) => (
                    <MenuItem key={jobTitle.value} value={jobTitle.value}>
                      {jobTitle.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Button
              variant="outlined"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              sx={{ minWidth: 130, whiteSpace: 'nowrap' }}
            >
              Clear Filters
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
              Add Record
            </Button>
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 2, mb: 1 }}>
          <Box />
          {loadingOptions && <CircularProgress size={20} />}
        </Box>

        <TableContainer sx={{ maxHeight: '68vh', overflowX: 'auto', overflowY: 'auto' }}>
          <Table size="small" stickyHeader sx={{ minWidth: 1800 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: blipColumnWidths.full_name, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={sortKey === 'full_name'} direction={sortKey === 'full_name' ? sortDirection : 'asc'} onClick={() => handleSort('full_name')}>
                    Full Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: blipColumnWidths.job_title, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={sortKey === 'job_title'} direction={sortKey === 'job_title' ? sortDirection : 'asc'} onClick={() => handleSort('job_title')}>
                    Job Title
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: blipColumnWidths.location, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={sortKey === 'location_name'} direction={sortKey === 'location_name' ? sortDirection : 'asc'} onClick={() => handleSort('location_name')}>
                    Location
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: blipColumnWidths.clock_in_date, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={sortKey === 'clock_in_date'} direction={sortKey === 'clock_in_date' ? sortDirection : 'asc'} onClick={() => handleSort('clock_in_date')}>
                    Clock In
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: blipColumnWidths.clock_in_location, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Clock In Location
                </TableCell>
                <TableCell sx={{ width: blipColumnWidths.clock_out_date, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Clock Out
                </TableCell>
                <TableCell sx={{ width: blipColumnWidths.clock_out_location, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Clock Out Location
                </TableCell>
                <TableCell sx={{ width: blipColumnWidths.blip_type, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Blip Type
                </TableCell>
                <TableCell sx={{ width: blipColumnWidths.hours, fontWeight: 600, whiteSpace: 'nowrap' }} align="right">
                  Hours
                </TableCell>
                <TableCell sx={{ width: blipColumnWidths.hours_paid, fontWeight: 600, whiteSpace: 'nowrap' }} align="right">
                  Hours Paid
                </TableCell>
                <TableCell sx={{ width: blipColumnWidths.hourly_rate, fontWeight: 600, whiteSpace: 'nowrap' }} align="right">
                  Hourly Rate
                </TableCell>
                <TableCell sx={{ width: blipColumnWidths.total_amount, fontWeight: 600, whiteSpace: 'nowrap' }} align="right">
                  <TableSortLabel active={sortKey === 'total_amount'} direction={sortKey === 'total_amount' ? sortDirection : 'asc'} onClick={() => handleSort('total_amount')}>
                    Total Amount
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: blipColumnWidths.actions, fontWeight: 600, whiteSpace: 'nowrap' }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingRecords ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : displayRows.length ? (
                displayRows.map((record) => (
                  <TableRow key={record.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.full_name || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.job_title || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.location_name || record.location_id || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(record.clock_in_date, record.clock_in_time)}</TableCell>
                    <TableCell title={record.clock_in_location || ''} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.clock_in_location || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(record.clock_out_date, record.clock_out_time)}</TableCell>
                    <TableCell title={record.clock_out_location || ''} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.clock_out_location || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{record.blip_type || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">{formatHours(record.salaried_hour)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">{record.hours_paid === null || record.hours_paid === undefined || record.hours_paid === '' ? '-' : formatHours(record.hours_paid)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">{formatCurrency(record.hourly_rate)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">{formatCurrency(record.total_amount)}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton size="small" onClick={() => openEditDialog(record)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => openDeleteDialog(record)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 6 }}>
                    No Blip In records found for the selected filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <TextField
              value={pageInput}
              onChange={handlePageInputChange}
              onKeyDown={handlePageInputKeyDown}
              label="Page"
              size="small"
              sx={{ width: 90 }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              disabled={loadingRecords || !totalRecords}
            />
            <Typography variant="body2" color="text.secondary">
              {totalRecords ? `of ${totalPages}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalRecords ? `Showing ${showingStart}-${showingEnd} of ${totalRecords}` : 'No records to show'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Typography variant="body2" color="text.secondary">
              Net total amount: {formatCurrency(netTotalAmount)}
            </Typography>
            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
              showFirstButton
              showLastButton
              labelRowsPerPage=""
              labelDisplayedRows={() => ''}
              sx={{
                '.MuiTablePagination-toolbar': { p: 0 },
                '.MuiTablePagination-selectLabel': { display: 'none' },
                '.MuiTablePagination-displayedRows': { m: 0 },
              }}
            />
          </Box>
        </Box>
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>{formMode === 'edit' ? 'Edit Blip In Record' : 'Add Blip In Record'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError ? (
              <Typography color="error" variant="body2">
                {formError}
              </Typography>
            ) : null}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="First Name" value={formData.first_name} onChange={(event) => handleFormChange('first_name', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Last Name" value={formData.last_name} onChange={(event) => handleFormChange('last_name', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Job Title"
                  value={formData.job_title}
                  onChange={(event) => handleFormChange('job_title', event.target.value)}
                  disabled={loadingOptions}
                >
                  <MenuItem value="">
                    <em>Select Job Title</em>
                  </MenuItem>
                  {formData.job_title && !sortedJobTitles.some((jobTitle) => jobTitle.label === formData.job_title) && (
                    <MenuItem value={formData.job_title} disabled>
                      {formData.job_title}
                    </MenuItem>
                  )}
                  {sortedJobTitles.map((jobTitle) => (
                    <MenuItem key={jobTitle.value} value={jobTitle.label}>
                      {jobTitle.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Team" value={formData.team} onChange={(event) => handleFormChange('team', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Blip Type" value={formData.blip_type} onChange={(event) => handleFormChange('blip_type', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Payroll Reference" value={formData.payroll_reference} onChange={(event) => handleFormChange('payroll_reference', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Clock In Date" type="date" InputLabelProps={{ shrink: true }} value={formData.clock_in_date} onChange={(event) => handleFormChange('clock_in_date', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Clock In Time" type="time" InputLabelProps={{ shrink: true }} value={formData.clock_in_time} onChange={(event) => handleFormChange('clock_in_time', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="clock-in-location-label">Clock In Location</InputLabel>
                  <Select
                    labelId="clock-in-location-label"
                    value={formData.clock_in_location_id}
                    label="Clock In Location"
                    onChange={(event) => handleFormChange('clock_in_location_id', event.target.value)}
                  >
                    <MenuItem value="">Unassigned</MenuItem>
                    {sortedLocations.map((location) => (
                      <MenuItem key={location.location_id || location.name} value={location.location_id || ''}>
                        {getLocationLabel(location)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Clock Out Date" type="date" InputLabelProps={{ shrink: true }} value={formData.clock_out_date} onChange={(event) => handleFormChange('clock_out_date', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Clock Out Time" type="time" InputLabelProps={{ shrink: true }} value={formData.clock_out_time} onChange={(event) => handleFormChange('clock_out_time', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="clock-out-location-label">Clock Out Location</InputLabel>
                  <Select
                    labelId="clock-out-location-label"
                    value={formData.clock_out_location_id}
                    label="Clock Out Location"
                    onChange={(event) => handleFormChange('clock_out_location_id', event.target.value)}
                  >
                    <MenuItem value="">Unassigned</MenuItem>
                    {sortedLocations.map((location) => (
                      <MenuItem key={location.location_id || location.name} value={location.location_id || ''}>
                        {getLocationLabel(location)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Total Duration" value={formData.total_duration} onChange={(event) => handleFormChange('total_duration', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Total Excluding Breaks" value={formData.total_excluding_breaks} onChange={(event) => handleFormChange('total_excluding_breaks', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Hourly Rate" value={formData.hourly_rate} onChange={(event) => handleFormChange('hourly_rate', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Total Amount" value={formData.total_amount} onChange={(event) => handleFormChange('total_amount', event.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Notes" value={formData.notes} onChange={(event) => handleFormChange('notes', event.target.value)} multiline minRows={3} />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={savingRecord}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={savingRecord}>
            {savingRecord ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Delete Blip In Record</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {deleteTarget?.full_name || 'this record'}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={Boolean(deletingRecordId)}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={Boolean(deletingRecordId)}>
            {deletingRecordId ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageBlipInPage;
